import React, { useEffect, useState } from "react";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Loader2, Home, Car, Camera, Sparkles, Wand2, ImageIcon, Check, CheckCheck, Square, Eye, EyeOff, Pencil, X, Utensils, Wrench, Plus } from "lucide-react";

function PhotoSelectPanel({ project, listing, onChange }) {
  const all = listing.image_media_ids || [];
  const [pool, setPool] = useState(all);
  const [selected, setSelected] = useState(() => new Set(listing.selected_media_ids || all));
  const [busy, setBusy] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [bestBusy, setBestBusy] = useState(false);
  const initBrief = (project.settings && project.settings.brief) || {};
  const [bizType, setBizType] = useState(initBrief.business_type || "");
  const [focus, setFocus] = useState(initBrief.focus || "");
  const [cta, setCta] = useState(initBrief.cta || "");
  const [briefBusy, setBriefBusy] = useState(false);

  const assistBrief = async () => {
    setBriefBusy(true);
    try {
      const { data } = await api.post("/projects/brief/assist", {
        business_type: bizType, focus, cta, vertical: project.vertical, pid: project.id,
      });
      setBizType(data.business_type || bizType);
      setFocus(data.focus || focus);
      setCta(data.cta || cta);
      toast.success("Brief drafted — tweak anything you like");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "AI assist failed");
    } finally {
      setBriefBusy(false);
    }
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const setAll = (on) => setSelected(on ? new Set(pool) : new Set());

  const addPhotos = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setAddBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ids = up.data.uploaded.map((u) => u.media_id);
      await api.post(`/projects/${project.id}/photos/add`, { media_ids: ids });
      setPool((p) => [...p, ...ids.filter((m) => !p.includes(m))]);
      setSelected((s) => { const n = new Set(s); ids.forEach((m) => n.add(m)); return n; });
      toast.success(`Added ${ids.length} photo${ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setAddBusy(false);
      e.target.value = "";
    }
  };

  const bestShots = async () => {
    setBestBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/photos/auto-select`, { count: 15 });
      if (data.selected && data.selected.length) {
        setSelected(new Set(data.selected));
        toast.success(`AI picked the best ${data.count} shots — tweak as you like`);
      } else {
        toast.error("Couldn't auto-pick — select manually");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Best shots failed");
    } finally {
      setBestBusy(false);
    }
  };

  const proceed = async () => {
    if (selected.size === 0) return toast.error("Select at least one photo");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { business_type: bizType, focus, cta });
      await api.post(`/projects/${project.id}/photos/select`, { media_ids: Array.from(selected) });
      await api.post(`/projects/${project.id}/run`);
      toast.success(`Analyzing ${selected.size} photos`);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up" data-testid="photo-select-panel">
      <div className="mb-6 border border-zinc-800 rounded-sm p-4 bg-zinc-950/40" data-testid="brief-editor">
        <div className="flex items-center justify-between mb-1">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-amber-500">Campaign Brief</p>
          <button
            type="button"
            data-testid="brief-edit-ai-assist"
            onClick={assistBrief}
            disabled={briefBusy}
            className="flex items-center gap-1.5 text-xs text-amber-500 hover:text-amber-400 border border-amber-500/40 hover:border-amber-400 rounded-sm px-2.5 py-1 transition-colors disabled:opacity-50"
          >
            {briefBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {briefBusy ? "Writing..." : "Write it for me"}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          What are you selling and what's the goal? This steers how the photos are described, what the script pitches, and the ending call-to-action. Not sure? Tap "Write it for me" — it'll use this business's photos/page. Edit before you continue.
        </p>
        <div className="grid gap-3">
          <Input data-testid="brief-edit-business-type" value={bizType} onChange={(e) => setBizType(e.target.value)}
            placeholder="Business type — e.g. Landscaper, Optician, Café" className="bg-zinc-950 border-zinc-800 rounded-sm h-11 text-sm" />
          <Textarea data-testid="brief-edit-focus" value={focus} onChange={(e) => setFocus(e.target.value)}
            placeholder="What you're promoting / focus — e.g. Expert lawn installs & retaining walls. Push fall cleanups. Talk about the quality of the work, not the scenery."
            className="bg-zinc-950 border-zinc-800 rounded-sm text-sm min-h-20" />
          <Input data-testid="brief-edit-cta" value={cta} onChange={(e) => setCta(e.target.value)}
            placeholder="Call to action — e.g. Book your fall cleanup, call today!" className="bg-zinc-950 border-zinc-800 rounded-sm h-11 text-sm" />
        </div>
      </div>
      <div className="flex items-center gap-2 text-amber-500 mb-2">
        <Sparkles className="w-4 h-4" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">Curate Photos</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl font-medium">Choose the shots to feature</h3>
          <p className="text-sm text-zinc-500 mt-1">
            {pool.length} photos scraped. Tap <span className="text-amber-400">Best shots</span> to let AI pre-pick the strongest ~15, then tweak. Use <span className="text-amber-400">Add photos</span> to upload your own (e.g. a custom agent backdrop). Drop near-duplicates so the tour shows the whole home — not the same room five times.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={bestShots} disabled={bestBusy} data-testid="best-shots" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-amber-500/50 text-amber-400 hover:border-amber-400 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
            {bestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            {bestBusy ? "Picking..." : "Best shots"}
          </button>
          <label data-testid="add-photos" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/60 cursor-pointer transition-colors">
            {addBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {addBusy ? "Adding..." : "Add photos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={addPhotos} disabled={addBusy} />
          </label>
          <button onClick={() => setAll(true)} data-testid="select-all" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            <CheckCheck className="w-3.5 h-3.5" /> All
          </button>
          <button onClick={() => setAll(false)} data-testid="select-none" className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">
            <Square className="w-3.5 h-3.5" /> None
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {pool.map((id, i) => {
          const on = selected.has(id);
          return (
            <button
              key={id}
              data-testid={`photo-toggle-${i}`}
              onClick={() => toggle(id)}
              className={`group relative rounded-sm overflow-hidden border-2 transition-all ${
                on ? "border-amber-500" : "border-zinc-800 opacity-45 hover:opacity-80"
              }`}
            >
              <img src={mediaUrl(id)} alt="" className="w-full h-32 object-cover" />
              <span className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                on ? "bg-amber-500" : "bg-zinc-900/80 border border-zinc-600"
              }`}>
                {on && <Check className="w-3.5 h-3.5 text-zinc-950" />}
              </span>
            </button>
          );
        })}
      </div>

      <div className="sticky bottom-4 mt-6 flex justify-center">
        <Button
          data-testid="analyze-photos-button"
          onClick={proceed}
          disabled={busy || selected.size === 0}
          className="h-12 px-8 rounded-full bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors shadow-lg shadow-amber-500/20"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Analyze {selected.size} photo{selected.size === 1 ? "" : "s"} & continue</>}
        </Button>
      </div>
    </div>
  );
}

function ManualPanel({ project, onChange }) {
  const isDealer = project.vertical === "dealership";
  const isPhotographer = project.vertical === "photographer";
  const isGeneral = project.vertical === "general";
  const isRestaurant = project.vertical === "restaurant";
  const isTrades = project.vertical === "trades";
  const [form, setForm] = useState({
    address: "", price: "", beds: "", baths: "", sqft: "", description: "",
    year: "", make: "", model: "", trim: "", mileage: "", ext_color: "", int_color: "", vin: "", stock: "",
    event_type: "", venue: "", client_names: "", event_date: "", business_name: "",
    cuisine: "", neighborhood: "", trade_type: "", service_area: "",
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!files.length) return toast.error("Upload at least one photo");
    setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));
      const up = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ids = up.data.uploaded.map((u) => u.media_id);
      await api.post(`/projects/${project.id}/listing/manual`, { ...form, image_media_ids: ids });
      await api.post(`/projects/${project.id}/run`);
      toast.success("Listing saved — pipeline running");
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 fade-up" data-testid="manual-listing-panel">
      <h3 className="font-display text-xl font-medium mb-1">{isDealer ? "Manual Vehicle" : isRestaurant ? "Manual Restaurant" : isTrades ? "Manual Trades Business" : isPhotographer ? "Manual Photo Story" : isGeneral ? "Manual Project" : "Manual Listing"}</h3>
      <p className="text-sm text-zinc-500 mb-6">
        {isDealer
          ? "Paste the vehicle details and upload photos here. Auto-scraping is attempted for URLs, but many dealer sites block automated access."
          : isRestaurant
          ? "Add your restaurant details and upload your best food & interior photos here. You can also paste your website, Instagram or Google Maps URL on the New Project screen to pull photos automatically."
          : isTrades
          ? "Add your business details and upload your best finished-work and before/after photos here. You can also paste your website, Instagram or Google Maps URL on the New Project screen to pull photos automatically."
          : isPhotographer
          ? "Add the story details and upload the photos you want in the reel. You can pull from several shoots — just upload the best shots here."
          : isGeneral
          ? "Add a business name and upload the photos you want in the reel. You can also paste an Instagram, Facebook or Google Maps URL on the New Project screen to pull photos automatically."
          : "Realtor.ca blocks automated access from cloud servers, so paste the details and upload the photos here. To enable automatic URL scraping, add a scraping-proxy API key on the server."}
      </p>
      {isDealer ? (
        <>
          <div className="grid sm:grid-cols-4 gap-4">
            <Input data-testid="manual-year" value={form.year} onChange={set("year")} placeholder="Year" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-make" value={form.make} onChange={set("make")} placeholder="Make" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-model" value={form.model} onChange={set("model")} placeholder="Model" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-trim" value={form.trim} onChange={set("trim")} placeholder="Trim" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          </div>
          <div className="grid sm:grid-cols-4 gap-4 mt-4">
            <Input data-testid="manual-price" value={form.price} onChange={set("price")} placeholder="Price" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-mileage" value={form.mileage} onChange={set("mileage")} placeholder="Mileage" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-ext-color" value={form.ext_color} onChange={set("ext_color")} placeholder="Exterior color" className="bg-zinc-950 border-zinc-800 rounded-sm" />
            <Input data-testid="manual-stock" value={form.stock} onChange={set("stock")} placeholder="Stock # (optional)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          </div>
        </>
      ) : isPhotographer ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="manual-client-names" value={form.client_names} onChange={set("client_names")} placeholder="Client / couple names (e.g. Sarah & James)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-event-type" value={form.event_type} onChange={set("event_type")} placeholder="Event type (Wedding, Portrait...)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-venue" value={form.venue} onChange={set("venue")} placeholder="Venue / location" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-event-date" value={form.event_date} onChange={set("event_date")} placeholder="Date (optional)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
      ) : isRestaurant ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="manual-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Restaurant name" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-cuisine" value={form.cuisine} onChange={set("cuisine")} placeholder="Cuisine (e.g. Italian, Gastropub)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-neighborhood" value={form.neighborhood} onChange={set("neighborhood")} placeholder="Neighborhood / area (optional)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
      ) : isTrades ? (
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="manual-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Business name" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-trade-type" value={form.trade_type} onChange={set("trade_type")} placeholder="Trade (e.g. Plumbing, Roofing, HVAC)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-service-area" value={form.service_area} onChange={set("service_area")} placeholder="Service area (e.g. Greater Toronto)" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
      ) : isGeneral ? (
        <div className="grid sm:grid-cols-1 gap-4">
          <Input data-testid="manual-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Business / brand name" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="manual-address" value={form.address} onChange={set("address")} placeholder="Address" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input data-testid="manual-price" value={form.price} onChange={set("price")} placeholder="Price" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input value={form.beds} onChange={set("beds")} placeholder="Beds" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input value={form.baths} onChange={set("baths")} placeholder="Baths" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          <Input value={form.sqft} onChange={set("sqft")} placeholder="Sq ft" className="bg-zinc-950 border-zinc-800 rounded-sm" />
        </div>
      )}
      <Textarea data-testid="manual-description" value={form.description} onChange={set("description")} placeholder={isDealer ? "Vehicle description / features..." : isRestaurant ? "A short note about the food, signature dishes / what to highlight..." : isTrades ? "A short note about your services / what to highlight (licensed, insured, years in business)..." : isPhotographer ? "A short note about the day / the story..." : isGeneral ? "A short note about the business / what to highlight..." : "Listing description..."} className="mt-4 bg-zinc-950 border-zinc-800 rounded-sm min-h-24" />
      <label className="mt-4 flex items-center gap-3 border border-dashed border-zinc-700 rounded-sm p-4 cursor-pointer hover:border-amber-500/60 transition-colors">
        <Upload className="w-5 h-5 text-amber-500" />
        <span className="text-sm text-zinc-400">
          {files.length ? `${files.length} photo(s) selected` : (isDealer ? "Choose vehicle photos" : isRestaurant ? "Choose food & interior photos" : isTrades ? "Choose finished-work & before/after photos" : isPhotographer ? "Choose photos from your shoots" : isGeneral ? "Choose photos for the reel" : "Choose property photos")}
        </span>
        <input data-testid="manual-file-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => setFiles(e.target.files)} />
      </label>
      <Button data-testid="manual-submit" onClick={submit} disabled={busy} className="mt-6 h-11 rounded-sm bg-amber-500 text-zinc-950 font-semibold hover:bg-amber-400 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & Run"}
      </Button>
    </div>
  );
}

export default function ListingPanel({ project, onChange }) {
  const [listing, setListing] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [hideTitle, setHideTitle] = useState(!!(project.settings && project.settings.hide_title));

  useEffect(() => {
    api.get(`/projects/${project.id}/listing`).then((r) => setListing(r.data)).catch(() => {});
    api.get(`/projects/${project.id}/analysis`).then((r) => setAnalysis(r.data)).catch(() => {});
  }, [project.id, project.updated_at]);

  useEffect(() => {
    setHideTitle(!!(project.settings && project.settings.hide_title));
  }, [project.settings?.hide_title]);

  const saveName = async () => {
    setSavingName(true);
    try {
      await api.post(`/projects/${project.id}/settings`, { name: nameDraft.trim() });
      toast.success("Title updated");
      setEditingName(false);
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingName(false);
    }
  };

  const toggleHideTitle = async () => {
    const next = !hideTitle;
    setHideTitle(next);
    try {
      await api.post(`/projects/${project.id}/settings`, { hide_title: next });
      toast.success(next ? "Title hidden from the video" : "Title will show in the video");
      onChange && onChange();
    } catch (err) {
      setHideTitle(!next);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const hasListing = listing && listing.image_media_ids && listing.image_media_ids.length;

  if (project.status === "needs_manual" || (project.mode === "manual" && !hasListing)) {
    return <ManualPanel project={project} onChange={onChange} />;
  }

  if (hasListing && (project.status === "review_photos" || listing.photos_confirmed === false)) {
    return <PhotoSelectPanel project={project} listing={listing} onChange={onChange} />;
  }

  if (!hasListing) {
    return (
      <div className="border border-dashed border-zinc-800 rounded-sm p-12 text-center text-zinc-600" data-testid="listing-empty">
        No listing data yet. Run the pipeline to scrape the URL.
      </div>
    );
  }

  const items =
    analysis && analysis.items && analysis.items.length
      ? analysis.items
      : listing.image_media_ids.map((m, i) => ({ media_id: m, caption: "", order: i, room_type: "" }));

  const isDealer = project.vertical === "dealership";
  const isPhotographer = project.vertical === "photographer";
  const isGeneral = project.vertical === "general";
  const isRestaurant = project.vertical === "restaurant";
  const isTrades = project.vertical === "trades";
  const vehicleTitle = [listing.year, listing.make, listing.model, listing.trim]
    .map((x) => (x || "").toString().trim()).filter(Boolean).join(" ");
  const photoTitle = (listing.client_names || listing.event_type || "").trim();
  const generalTitle = (listing.business_name || listing.title || "").trim();

  const autoTitle = isDealer ? (vehicleTitle || listing.title || listing.address || "Vehicle")
    : isRestaurant ? (generalTitle || "The Menu")
    : isTrades ? (generalTitle || "Our Work")
    : isPhotographer ? (photoTitle || listing.title || "Photo Story")
    : isGeneral ? (generalTitle || "Highlights")
    : (listing.address || "Property");
  const displayTitle = (project.name || "").trim() || autoTitle;

  return (
    <div className="fade-up" data-testid="listing-panel">
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 mb-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 text-amber-500">
            {isDealer ? <Car className="w-4 h-4" /> : isRestaurant ? <Utensils className="w-4 h-4" /> : isTrades ? <Wrench className="w-4 h-4" /> : isPhotographer ? <Camera className="w-4 h-4" /> : isGeneral ? <Sparkles className="w-4 h-4" /> : <Home className="w-4 h-4" />}
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">{isDealer ? "Vehicle" : isRestaurant ? "Restaurant" : isTrades ? "Trades" : isPhotographer ? "Photo Story" : isGeneral ? "Project" : "Listing"}</span>
          </div>
          <button
            type="button"
            data-testid="toggle-hide-title"
            onClick={toggleHideTitle}
            title={hideTitle ? "Title is hidden from the rendered video — click to show it" : "Title shows on the rendered video — click to hide it"}
            className={`flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.15em] rounded-sm px-2.5 py-1.5 border transition-colors ${
              hideTitle
                ? "border-zinc-700 text-zinc-500 hover:border-zinc-600"
                : "border-amber-500/50 text-amber-400 hover:border-amber-400 bg-amber-500/10"
            }`}
          >
            {hideTitle ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {hideTitle ? "Title hidden" : "Title on video"}
          </button>
        </div>
        {editingName ? (
          <div className="flex items-center gap-2" data-testid="listing-name-editor">
            <Input
              data-testid="listing-name-input"
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
              placeholder="Project title"
              className="bg-zinc-950 border-zinc-700 rounded-sm h-11 text-xl font-display focus-visible:ring-amber-500/40 focus-visible:border-amber-500"
            />
            <button data-testid="listing-name-save" onClick={saveName} disabled={savingName}
              className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50 shrink-0">
              {savingName ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
            </button>
            <button data-testid="listing-name-cancel" onClick={() => setEditingName(false)} disabled={savingName}
              className="text-zinc-500 hover:text-zinc-300 disabled:opacity-50 shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-2 group/title">
            <h3 data-testid="listing-title" className="font-display text-2xl font-medium">
              {displayTitle}
            </h3>
            <button
              type="button"
              data-testid="listing-name-edit"
              onClick={() => { setNameDraft((project.name || "").trim() || autoTitle); setEditingName(true); }}
              title="Rename this project"
              className="mt-1.5 shrink-0 text-zinc-600 hover:text-amber-500 transition-colors opacity-0 group-hover/title:opacity-100"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="flex flex-wrap gap-4 mt-3 font-mono2 text-xs text-zinc-400">
          {listing.price && !isPhotographer && !isGeneral && <span>{listing.price}</span>}
          {isDealer ? (
            <>
              {listing.mileage && <span>{listing.mileage}</span>}
              {listing.ext_color && <span>{listing.ext_color}</span>}
              {listing.stock && <span>Stock #{listing.stock}</span>}
            </>
          ) : isPhotographer ? (
            <>
              {listing.event_type && <span>{listing.event_type}</span>}
              {listing.venue && <span>{listing.venue}</span>}
              {listing.event_date && <span>{listing.event_date}</span>}
            </>
          ) : isGeneral ? (
            <>
              {listing.source && <span className="capitalize">{String(listing.source).replace("social:", "")}</span>}
            </>
          ) : (
            <>
              {listing.beds && <span>{listing.beds} bd</span>}
              {listing.baths && <span>{listing.baths} ba</span>}
              {listing.sqft && <span>{listing.sqft} sqft</span>}
            </>
          )}
          {!isGeneral && <span className="text-zinc-600">{listing.source}</span>}
        </div>
        {listing.description && (
          <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-3xl">{listing.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2 text-zinc-500 mb-4">
        <ImageIcon className="w-4 h-4" />
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em]">
          Ordered Gallery ({items.length})
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((it, i) => (
          <div key={i} className="group relative rounded-sm overflow-hidden border border-zinc-800" data-testid={`gallery-item-${i}`}>
            <img src={mediaUrl(it.media_id)} alt="" className="w-full h-40 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
              <span className="font-mono2 text-[9px] uppercase tracking-widest text-amber-400">
                #{i + 1} {it.room_type}
              </span>
              <span className="text-xs text-zinc-200 mt-1 line-clamp-2">{it.caption}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
